import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usersAPI, formatError } from "../utils/api";
import { useConfirm } from "../hooks/useConfirm";

import {
  Plus, Edit, Trash2, Key, X, Eye, Pencil, ShieldCheck, ChevronDown, ChevronRight,
  LayoutDashboard, Users, Truck, Shield, Container, IndianRupee, Wrench, Activity,
  Package, ShoppingCart, CheckSquare, Settings, BarChart3, UtensilsCrossed, Search, Filter
} from "lucide-react";

// Module-permission mapping: view, create/edit, manage/delete
// Sub-module key format: sub_{module}_{submodule}_{action}
// e.g. sub_security_entry_exit_view, sub_stores_stock_inward_edit

const MODULE_PERMS = [
  {
    module: "Dashboard", icon: LayoutDashboard, view: "dashboard", edit: null, manage: null, color: "text-blue-500",
    subModules: []
  },
  {
    module: "Security / Gate", icon: Shield, view: "security_view", edit: "security_create", manage: null, color: "text-blue-600",
    subModules: [
      { name: "Entry / Exit",         key: "security_entry_exit",   hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Gate Log",              key: "security_gate_log",     hasView: true, hasEdit: false, hasApprove: false },
    ]
  },
  {
    module: "Daily Operations", icon: Activity, view: null, edit: null, manage: null, color: "text-indigo-500",
    subModules: [
      { name: "Daily Entry Hub",       key: "ops_daily_entry",       hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Vessel Management",     key: "ops_vessels",           hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Vessel Entry",          key: "ops_vessel_entry",      hasView: true, hasEdit: true,  hasApprove: false },
      { name: "School Indent",         key: "ops_school_indent",     hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "MDM Reconciliation",    key: "ops_reconciliation",    hasView: true, hasEdit: false, hasApprove: false },
    ]
  },
  {
    module: "Kitchen & Menu", icon: UtensilsCrossed, view: "menu_view", edit: "menu_create", manage: "menu_manage", color: "text-pink-500",
    subModules: [
      { name: "Production Planning",   key: "kitchen_production",    hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Menu Items",            key: "kitchen_menu_items",    hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Recipes",               key: "kitchen_recipes",       hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Daily Plan",            key: "kitchen_daily_plan",    hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Stock Calculator",      key: "kitchen_calculator",    hasView: true, hasEdit: false, hasApprove: false },
    ]
  },
  {
    module: "Stores", icon: Package, view: "stores_view", edit: "stores_create", manage: "stores_manage", color: "text-green-600",
    subModules: [
      { name: "Stock Inward",          key: "stores_inward",         hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Stock Issued",          key: "stores_issued",         hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Reject Stock",          key: "stores_reject",         hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Stock Outward",         key: "stores_outward",        hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Min Stock Alerts",      key: "stores_alerts",         hasView: true, hasEdit: false, hasApprove: false },
      { name: "Vehicle Usage",         key: "stores_vehicle_usage",  hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Purchase Requests",     key: "stores_purchase_req",   hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Stock Summary",         key: "stores_summary",        hasView: true, hasEdit: false, hasApprove: false },
    ]
  },
  {
    module: "Purchase & Vendors", icon: ShoppingCart, view: "purchase_view", edit: null, manage: "purchase_manage", color: "text-teal-600",
    subModules: [
      { name: "Indents Received",      key: "purchase_indents",      hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Purchase Orders",       key: "purchase_orders",       hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Vendor Management",     key: "purchase_vendors",      hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Rate Contracts",        key: "purchase_contracts",    hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Quote Requests",        key: "purchase_quotes",       hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Supplier Scorecard",    key: "purchase_scorecard",    hasView: true, hasEdit: false, hasApprove: false },
      { name: "Gmail (Vendor)",        key: "purchase_gmail",        hasView: true, hasEdit: true,  hasApprove: false },
    ]
  },
  {
    module: "Logistics", icon: Truck, view: "logistics_view", edit: null, manage: "logistics_manage", color: "text-amber-600",
    subModules: [
      { name: "Routes & Vehicles",     key: "logistics_routes",      hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Route Coordinators",    key: "logistics_coordinators",hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "School Master",         key: "logistics_schools",     hasView: true, hasEdit: false, hasApprove: false },
      { name: "School Master (Edit)",  key: "school_master_edit",    hasView: false,hasEdit: true,  hasApprove: false },
      { name: "Route Assignment Edit", key: "route_assignment_edit", hasView: false,hasEdit: true,  hasApprove: true  },
      { name: "Vehicle Master Edit",   key: "vehicle_master_edit",   hasView: false,hasEdit: true,  hasApprove: false },
      { name: "Temp Assignments",      key: "temp_assignment_edit",  hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Logistics Reports",     key: "logistics_reports",     hasView: true, hasEdit: false, hasApprove: false },
      { name: "Temp Assignments",      key: "logistics_temp",        hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Vehicle Maintenance",   key: "logistics_maintenance", hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Fuel Log",              key: "logistics_fuel",        hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Route Planner",         key: "route_planner",         hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Delivery Tracking",     key: "logistics_delivery",    hasView: true, hasEdit: false, hasApprove: false },
      { name: "School Indent",         key: "logistics_indent",      hasView: true, hasEdit: true,  hasApprove: false },
      { name: "MDM Reconciliation",    key: "logistics_reconcile",   hasView: true, hasEdit: false, hasApprove: false },
    ]
  },
  {
    module: "Finance", icon: IndianRupee, view: "admin_view", edit: null, manage: "admin_manage", color: "text-orange-500",
    subModules: [
      { name: "Overview",              key: "finance_overview",    hasView: true, hasEdit: false, hasApprove: false },
      { name: "Billing Statements",    key: "finance_statements",  hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "DEO Indent Import",     key: "finance_deo_import",  hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Billing Rates",         key: "finance_rates",       hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Utility Costs",         key: "finance_utility",     hasView: true, hasEdit: true,  hasApprove: false },
    ]
  },
  {
    module: "People & HR", icon: Users, view: "hr_view", edit: null, manage: null, color: "text-purple-500",
    subModules: [
      { name: "Employee Master",       key: "hr_employees",          hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Workers List",          key: "hr_workers",            hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Attendance",            key: "hr_attendance",         hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "OT Reports",            key: "hr_ot",                 hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Holiday Calendar",      key: "hr_holidays",           hasView: true, hasEdit: true,  hasApprove: false },
    ]
  },
  {
    module: "Assets & Quality", icon: Wrench, view: null, edit: null, manage: null, color: "text-gray-600",
    subModules: [
      { name: "Asset Management",      key: "assets_main",           hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Quality Inspection",    key: "assets_quality",        hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Inspection Visits",     key: "assets_inspections",    hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Field Visits",          key: "assets_field",          hasView: true, hasEdit: true,  hasApprove: false },
      { name: "School Compliance",     key: "assets_compliance",     hasView: true, hasEdit: true,  hasApprove: true  },
    ]
  },
  {
    module: "Reports & AI", icon: BarChart3, view: "reports_view", edit: null, manage: null, color: "text-emerald-600",
    subModules: [
      { name: "AI Analytics (Manna)",  key: "reports_ai",            hasView: true, hasEdit: false, hasApprove: false },
      { name: "Reports",               key: "reports_main",          hasView: true, hasEdit: false, hasApprove: false },
      { name: "Deletion Log",          key: "reports_deletion",      hasView: true, hasEdit: false, hasApprove: true  },
    ]
  },
  {
    module: "Tasks", icon: CheckSquare, view: "tasks_view", edit: null, manage: "tasks_manage", color: "text-violet-500",
    subModules: []
  },
  {
    module: "Settings", icon: Settings, view: "users_view", edit: null, manage: "users_manage", color: "text-[#E8956D]",
    subModules: [
      { name: "User Management",       key: "settings_users",        hasView: true, hasEdit: true,  hasApprove: true  },
      { name: "Admin Settings",        key: "settings_admin",        hasView: true, hasEdit: true,  hasApprove: false },
      { name: "Report Formats",        key: "settings_report_fmt",   hasView: true, hasEdit: true,  hasApprove: false },
    ]
  },
];

// Toggle switch component
function Toggle({ checked, onChange, color = "bg-[#E8956D]", disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 focus:outline-none
        ${checked ? color : "bg-[#E5E1DB]"} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:opacity-90"}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200
        ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

// Permission Matrix for role form — expandable sub-modules
function PermissionMatrix({ permissions, onChange }) {
  const [expanded, setExpanded] = useState({});
  const getVal  = (key) => key ? !!(permissions?.[key]) : false;
  const setVal  = (key, val) => {
    if (!key) return;
    const updated = { ...permissions, [key]: val };
    // If module view is turned off → turn off edit/manage too
    MODULE_PERMS.forEach(m => {
      if (m.view === key && !val) {
        if (m.edit) updated[m.edit] = false;
        if (m.manage) updated[m.manage] = false;
        // Also turn off all sub-module permissions for this module
        (m.subModules || []).forEach(s => {
          updated[`sub_${s.key}_view`]    = false;
          updated[`sub_${s.key}_edit`]    = false;
          updated[`sub_${s.key}_approve`] = false;
        });
      }
      // If edit/manage turned on → ensure view is on
      if ((m.edit === key || m.manage === key) && val) {
        if (m.view) updated[m.view] = true;
      }
    });
    onChange(updated);
  };

  // Sub-module helpers
  const getSubVal = (subKey, action) => !!(permissions?.[`sub_${subKey}_${action}`]);
  const setSubVal = (subKey, action, val) => {
    const permKey = `sub_${subKey}_${action}`;
    const updated = { ...permissions, [permKey]: val };
    // If sub-view turned off → turn off edit and approve too
    if (action === "view" && !val) {
      updated[`sub_${subKey}_edit`]    = false;
      updated[`sub_${subKey}_approve`] = false;
    }
    // If edit/approve turned on → ensure sub-view is on
    if ((action === "edit" || action === "approve") && val) {
      updated[`sub_${subKey}_view`] = true;
    }
    onChange(updated);
  };

  const toggleExpand = (module) => setExpanded(prev => ({ ...prev, [module]: !prev[module] }));

  return (
    <div className="border border-[#E5E1DB] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[1fr_auto_auto_auto] bg-[#F4F3F0] px-4 py-2.5 gap-4">
        <span className="text-xs font-bold uppercase tracking-wider text-[#5C5147]">Module / Sub-module</span>
        <span className="text-xs font-bold uppercase tracking-wider text-blue-600 w-16 text-center">View</span>
        <span className="text-xs font-bold uppercase tracking-wider text-[#E8956D] w-16 text-center">Edit</span>
        <span className="text-xs font-bold uppercase tracking-wider text-[#C2624E] w-16 text-center">Approve</span>
      </div>

      <div className="divide-y divide-[#F4F3F0]">
        {MODULE_PERMS.map(({ module, icon: Icon, view, edit, manage, color, subModules }) => {
          const viewOn  = getVal(view);
          const hasSubs = subModules && subModules.length > 0;
          const isOpen  = !!expanded[module];

          return (
            <div key={module}>
              {/* Module row */}
              <div className={`grid grid-cols-[1fr_auto_auto_auto] items-center px-4 py-3 gap-4 transition-colors ${hasSubs ? "cursor-pointer hover:bg-[#F4F3F0]" : "hover:bg-[#FAFAF8]"} ${isOpen ? "bg-[#F8F8F6]" : ""}`}
                onClick={() => hasSubs && toggleExpand(module)}>
                <div className="flex items-center gap-2.5">
                  {hasSubs ? (
                    <span className="text-[#8A7E73]">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                  ) : <span className="w-3.5" />}
                  <div className={`w-7 h-7 rounded-lg bg-[#F4F3F0] flex items-center justify-center ${color}`}>
                    <Icon size={15} strokeWidth={2} />
                  </div>
                  <span className="text-sm font-semibold text-[#3A322C]">{module}</span>
                  {hasSubs && (
                    <span className="text-[10px] text-[#8A7E73] bg-[#F4F3F0] px-1.5 py-0.5 rounded-full">{subModules.length} sub-modules</span>
                  )}
                </div>
                <div className="w-16 flex justify-center" onClick={e => e.stopPropagation()}>
                  {view ? <Toggle checked={viewOn} onChange={v => setVal(view, v)} color="bg-blue-500" /> : <span className="text-[#C4BAAF] text-xs">—</span>}
                </div>
                <div className="w-16 flex justify-center" onClick={e => e.stopPropagation()}>
                  {edit ? <Toggle checked={getVal(edit)} onChange={v => setVal(edit, v)} color="bg-[#E8956D]" disabled={!viewOn} /> : <span className="text-[#C4BAAF] text-xs">—</span>}
                </div>
                <div className="w-16 flex justify-center" onClick={e => e.stopPropagation()}>
                  {manage ? <Toggle checked={getVal(manage)} onChange={v => setVal(manage, v)} color="bg-[#C2624E]" disabled={!viewOn} /> : <span className="text-[#C4BAAF] text-xs">—</span>}
                </div>
              </div>

              {/* Sub-module rows — shown when expanded */}
              {hasSubs && isOpen && (
                <div className="bg-[#FAFAF8] border-t border-[#F4F3F0]">
                  {subModules.map(sub => {
                    const subViewOn = getSubVal(sub.key, "view");
                    return (
                      <div key={sub.key} className="grid grid-cols-[1fr_auto_auto_auto] items-center pl-14 pr-4 py-2.5 gap-4 hover:bg-[#F4F3F0] border-b border-[#F4F3F0] last:border-0 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="text-[#C4BAAF] text-xs">└</span>
                          <span className="text-xs font-medium text-[#5C5147]">{sub.name}</span>
                        </div>
                        <div className="w-16 flex justify-center">
                          {sub.hasView
                            ? <Toggle checked={subViewOn} onChange={v => setSubVal(sub.key, "view", v)} color="bg-blue-400" />
                            : <span className="text-[#C4BAAF] text-xs">—</span>}
                        </div>
                        <div className="w-16 flex justify-center">
                          {sub.hasEdit
                            ? <Toggle checked={getSubVal(sub.key, "edit")} onChange={v => setSubVal(sub.key, "edit", v)} color="bg-amber-400" disabled={!subViewOn} />
                            : <span className="text-[#C4BAAF] text-xs">—</span>}
                        </div>
                        <div className="w-16 flex justify-center">
                          {sub.hasApprove
                            ? <Toggle checked={getSubVal(sub.key, "approve")} onChange={v => setSubVal(sub.key, "approve", v)} color="bg-red-400" disabled={!subViewOn} />
                            : <span className="text-[#C4BAAF] text-xs">—</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 bg-[#FAFAF8] border-t border-[#E5E1DB]">
        <div className="flex items-center gap-1.5 text-xs text-[#8A7E73]"><div className="w-3 h-3 rounded-full bg-blue-500" /><span>View — can see</span></div>
        <div className="flex items-center gap-1.5 text-xs text-[#8A7E73]"><div className="w-3 h-3 rounded-full bg-[#E8956D]" /><span>Edit — can create/edit</span></div>
        <div className="flex items-center gap-1.5 text-xs text-[#8A7E73]"><div className="w-3 h-3 rounded-full bg-[#C2624E]" /><span>Approve — can delete/approve</span></div>
        <div className="flex items-center gap-1.5 text-xs text-[#8A7E73]"><ChevronRight size={12} /><span>Click module to expand sub-modules</span></div>
      </div>
    </div>
  );
}

// Permission summary badges for role card
function PermissionBadges({ permissions }) {
  const active = MODULE_PERMS.filter(m => permissions?.[m.view] || permissions?.[m.edit] || permissions?.[m.manage]);
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {active.map(m => {
        const hasManage = m.manage && permissions?.[m.manage];
        const hasEdit = m.edit && permissions?.[m.edit];
        const hasView = m.view && permissions?.[m.view];
        const level = hasManage ? "approve" : hasEdit ? "edit" : "view";
        const colors = { view: "bg-blue-50 text-blue-700", edit: "bg-[#FDF4EB] text-[#D98032]", approve: "bg-[#FBEAE7] text-[#C2624E]" };
        return (
          <span key={m.module} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colors[level]}`}>
            {m.module}
          </span>
        );
      })}
    </div>
  );
}

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white z-10">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose} className="text-[#8A7E73] hover:text-[#3A322C]"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const tabs = [
  { id: "Users",    label: "Users",    subKey: "settings_users" },
  { id: "Roles",    label: "Roles",    subKey: "settings_users" },
  { id: "Kitchens", label: "Kitchens", subKey: "settings_admin" },
];

export default function UserManagement() {
  const { user } = useAuth();
  const { confirm, ConfirmModal } = useConfirm();
  const [activeTab, setActiveTab] = useState("Users");
  // Fallback: if active tab is hidden by permissions, switch to first visible tab
  useEffect(() => {
    const visible = tabs.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false);
    if (visible.length > 0 && !visible.find(t => t.id === activeTab)) setActiveTab(visible[0].id);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [resetPwdModal, setResetPwdModal] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("active"); // "active" | "inactive" | "all"

  // Derived: filtered user list
  const filteredUsers = useMemo(() => {
    let list = users;
    // Status filter
    if (filterStatus === "active") list = list.filter(u => u.is_active !== false);
    else if (filterStatus === "inactive") list = list.filter(u => u.is_active === false);
    // Role filter
    if (filterRole) list = list.filter(u => u.role_id === filterRole || u.role_name === filterRole);
    // Search: name, email, phone
    if (userSearch.trim()) {
      const q = userSearch.trim().toLowerCase();
      list = list.filter(u =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.phone || "").includes(q)
      );
    }
    return list;
  }, [users, filterStatus, filterRole, userSearch]);

  useEffect(() => { fetchAll(); }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, r, k] = await Promise.all([
        usersAPI.getAll(filterStatus === "inactive" || filterStatus === "all" ? { include_inactive: true } : {}),
        usersAPI.getRoles(),
        usersAPI.getKitchens()
      ]);
      setUsers(u.data || []);
      setRoles(r.data || []);
      setKitchens(k.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleReactivate = async (id, name) => {
    try {
      await usersAPI.reactivate(id);
      fetchAll();
    } catch (e) { alert(formatError(e)); }
  };

  const openCreate = (type) => {
    if (type === "role") {
      setForm({ permissions: { dashboard: true, tasks_view: true } });
    } else if (type === "kitchen") {
      setForm({});
    } else {
      setForm({ kitchen_ids: [] });
    }
    setModal({ type, mode: "create" });
    setError("");
  };

  const openEdit = (type, item) => {
    if (type === "role") setForm({ ...item, permissions: item.permissions || {} });
    else if (type === "kitchen") setForm({ ...item });
    else setForm({ ...item, kitchen_ids: item.kitchen_ids || [] });
    setModal({ type, mode: "edit", id: item.id });
    setError("");
  };

  const handleSave = async () => {
    setError("");
    // For user creation, require at least name + password + (email or phone)
    if (modal.type === "user" && modal.mode === "create") {
      if (!form.name?.trim()) { setError("Full name is required"); return; }
      if (!form.password?.trim()) { setError("Password is required"); return; }
      if (!form.email?.trim() && !form.phone?.trim()) { setError("Either email or phone number is required"); return; }
    }
    try {
      if (modal.type === "user") {
        if (modal.mode === "create") await usersAPI.create(form);
        else await usersAPI.update(modal.id, form);
      } else if (modal.type === "role") {
        if (modal.mode === "create") await usersAPI.createRole(form);
        else await usersAPI.updateRole(modal.id, form);
      } else if (modal.type === "kitchen") {
        if (modal.mode === "create") await usersAPI.createKitchen(form);
        else await usersAPI.updateKitchen(modal.id, form);
      }
      setModal(null);
      fetchAll();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (type, id) => {
    if (!await confirm("Are you sure?")) return;
    try {
      if (type === "user") await usersAPI.delete(id);
      else if (type === "role") await usersAPI.deleteRole(id);
      else if (type === "kitchen") await usersAPI.deleteKitchen(id);
      fetchAll();
    } catch (e) { alert(formatError(e)); }
  };

  const handleResetPwd = async () => {
    setPwdError("");
    if (!newPwd || newPwd.length < 6) { setPwdError("Password must be at least 6 characters"); return; }
    try {
      await usersAPI.resetPassword(resetPwdModal, newPwd);
      setResetPwdModal(null);
      setNewPwd("");
    } catch (e) { setPwdError(formatError(e)); }
  };

  if (!user?.is_admin) return <div className="text-center py-20 text-[#8A7E73]">Admin access required</div>;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="user-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">User Management</h1>
        <button
          onClick={() => openCreate(activeTab.toLowerCase().slice(0, -1) === "kitch" ? "kitchen" : activeTab.toLowerCase().slice(0, -1))}
          className="bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661] rounded-lg px-4 py-2 font-bold flex items-center gap-2"
          data-testid="create-user-btn"
        >
          <Plus size={16} /> Add {activeTab.slice(0, -1)}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB]">
        {tabs.filter(t => !t.subKey || user?.is_admin || user?.permissions?.[`sub_${t.subKey}_view`] !== false).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* USERS */}
          {activeTab === "Users" && (
            <div className="space-y-3">
              {/* Search + Filter bar */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-48">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                  <input
                    type="text"
                    placeholder="Search by name, email or phone..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    data-testid="user-search-input"
                  />
                </div>
                {/* Role filter */}
                <div className="relative">
                  <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                  <select
                    value={filterRole}
                    onChange={e => setFilterRole(e.target.value)}
                    className="border border-[#E5E1DB] rounded-lg pl-8 pr-8 py-2 text-sm outline-none focus:border-[#E8956D] bg-white appearance-none min-w-[150px]"
                    data-testid="user-role-filter"
                  >
                    <option value="">All Roles</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                {/* Status filter */}
                <div className="flex gap-1 bg-[#F4F3F0] p-1 rounded-lg">
                  {[["active","Active"],["inactive","Inactive"],["all","All"]].map(([val, lbl]) => (
                    <button key={val} onClick={() => setFilterStatus(val)}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${filterStatus === val ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73] hover:text-[#5C5147]"}`}
                      data-testid={`status-filter-${val}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {/* Clear filters */}
                {(userSearch || filterRole || filterStatus !== "active") && (
                  <button onClick={() => { setUserSearch(""); setFilterRole(""); setFilterStatus("active"); }}
                    className="text-xs text-[#E8956D] hover:underline font-semibold">
                    Clear
                  </button>
                )}
              </div>

              {/* Table */}
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b border-[#F4F3F0] bg-[#FAFAF8]">
                  <span className="text-xs text-[#8A7E73]">
                    {filteredUsers.length} of {users.length} user(s)
                    {filterRole && ` · Role: ${roles.find(r => r.id === filterRole)?.name || filterRole}`}
                    {filterStatus !== "active" && ` · Status: ${filterStatus}`}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Name", "Email / Phone", "Role", "Kitchen", "Status", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {filteredUsers.map(u => (
                        <tr key={u.id} className={`hover:bg-[#FAFAF8] ${u.is_active === false ? "opacity-60" : ""}`}>
                          <td className="px-4 py-3 font-medium text-[#3A322C]">
                            {u.name} {u.is_admin && <span className="text-[10px] bg-[#E8956D]/20 text-[#E8956D] px-1.5 py-0.5 rounded-full font-bold ml-1">ADMIN</span>}
                          </td>
                          <td className="px-4 py-3 text-[#5C5147]">
                            {u.email
                              ? u.email
                              : u.phone
                                ? <span className="text-xs"><span className="text-[#8A7E73]">Phone:</span> {u.phone}</span>
                                : <span className="text-[#C4BAAF] italic text-xs">—</span>
                            }
                          </td>
                          <td className="px-4 py-3">
                            {u.role_name
                              ? <span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full font-medium">{u.role_name}</span>
                              : <span className="text-[#C4BAAF] text-xs">—</span>
                            }
                          </td>
                          <td className="px-4 py-3 text-[#8A7E73] text-xs">{u.kitchen_ids?.length > 0 ? `${u.kitchen_ids.length} kitchen(s)` : "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.is_active !== false ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                              {u.is_active !== false ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {u.is_active !== false ? (
                                <>
                                  <button onClick={() => openEdit("user", u)} className="text-[#8A7E73] hover:text-[#E8956D]" title="Edit"><Edit size={15} /></button>
                                  <button onClick={() => { setResetPwdModal(u.id); setNewPwd(""); setPwdError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]" title="Reset Password"><Key size={15} /></button>
                                  <button onClick={() => handleDelete("user", u.id)} className="text-[#8A7E73] hover:text-[#C2624E]" title="Delete"><Trash2 size={15} /></button>
                                </>
                              ) : (
                                <button onClick={() => handleReactivate(u.id, u.name)}
                                  className="text-xs bg-[#637C64] text-white px-3 py-1 rounded-lg font-bold hover:bg-[#4F6450]">
                                  Reactivate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredUsers.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">
                          {userSearch || filterRole ? "No users match your search/filter" : "No users found"}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ROLES */}
          {activeTab === "Roles" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roles.map(r => (
                <div key={r.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-[#3A322C] text-base">{r.name}</h3>
                        <span className="text-[10px] bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">
                          {Object.values(r.permissions || {}).filter(Boolean).length} perms
                        </span>
                      </div>
                      <p className="text-xs text-[#8A7E73] mb-2">{r.description}</p>
                      <PermissionBadges permissions={r.permissions || {}} />
                    </div>
                    <div className="flex gap-2 ml-3 flex-shrink-0">
                      <button onClick={() => openEdit("role", r)} className="text-[#8A7E73] hover:text-[#E8956D] p-1"><Edit size={15} /></button>
                      <button onClick={() => handleDelete("role", r.id)} className="text-[#8A7E73] hover:text-[#C2624E] p-1"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  {/* Mini permission grid */}
                  <div className="mt-3 grid grid-cols-3 gap-1">
                    {MODULE_PERMS.filter(m => (r.permissions || {})[m.view]).map(m => {
                      const Icon = m.icon;
                      const hasManage = m.manage && (r.permissions || {})[m.manage];
                      const hasEdit = m.edit && (r.permissions || {})[m.edit];
                      return (
                        <div key={m.module} className="flex items-center gap-1.5 bg-[#F4F3F0] rounded-lg px-2 py-1.5">
                          <Icon size={11} className={m.color} />
                          <span className="text-[10px] text-[#5C5147] truncate flex-1">{m.module}</span>
                          <div className="flex gap-0.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400" title="View" />
                            {hasEdit && <div className="w-1.5 h-1.5 rounded-full bg-[#E8956D]" title="Edit" />}
                            {hasManage && <div className="w-1.5 h-1.5 rounded-full bg-[#C2624E]" title="Approve" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {roles.length === 0 && <div className="text-center py-16 text-[#8A7E73] col-span-2">No roles defined</div>}
            </div>
          )}

          {/* KITCHENS */}
          {activeTab === "Kitchens" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kitchens.map(k => (
                <div key={k.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-[#3A322C]">{k.name}</h3>
                    <button onClick={() => openEdit("kitchen", k)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={15} /></button>
                  </div>
                  <p className="text-sm text-[#5C5147]">{k.district}, {k.state}</p>
                  <p className="text-xs text-[#8A7E73] mt-1">{k.address}</p>
                  <span className={`mt-2 inline-block text-xs font-bold px-2 py-0.5 rounded-full ${k.is_active ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                    {k.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ---- ROLE MODAL (Toggle Matrix) ---- */}
      {modal?.type === "role" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Role`} onClose={() => setModal(null)} wide>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Role Name *</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Description</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-3 block">Module Permissions</label>
              <PermissionMatrix
                permissions={form.permissions || {}}
                onChange={perms => setForm({ ...form, permissions: perms })}
              />
            </div>

            {/* Quick presets */}
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Quick Presets</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "View Only", perms: { dashboard: true, logistics_view: true, security_view: true, vessels_view: true, stores_view: true, purchase_view: true, tasks_view: true, reports_view: true, menu_view: true } },
                  { label: "Operations", perms: { dashboard: true, logistics_view: true, logistics_manage: true, security_view: true, security_create: true, vessels_view: true, stores_view: true, stores_create: true, purchase_view: true, tasks_view: true, menu_view: true, menu_create: true } },
                  { label: "Full Access", perms: Object.fromEntries([...MODULE_PERMS.flatMap(m => [m.view, m.edit, m.manage].filter(Boolean).map(k => [k, true]))]) },
                  { label: "Clear All", perms: {} },
                ].map(preset => (
                  <button key={preset.label} type="button"
                    onClick={() => setForm({ ...form, permissions: preset.perms })}
                    className="text-xs border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] font-medium">
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C] hover:bg-[#D48661]" data-testid="save-role-btn">Save Role</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---- USER MODAL ---- */}
      {modal?.type === "user" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} User`} onClose={() => setModal(null)}>
          {/* Hidden inputs trick — prevents browser autofill on the real fields */}
          <input type="text" className="hidden" aria-hidden="true" />
          <input type="email" className="hidden" aria-hidden="true" />
          <input type="password" className="hidden" aria-hidden="true" />
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Full Name *</label>
              <input autoComplete="off" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Email <span className="normal-case text-[10px] font-normal text-[#C4BAAF]">(or use phone below)</span></label>
              <input type="text" autoComplete="off" name="new-user-email" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="Leave blank if phone-only user" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            {modal.mode === "create" && (
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Password *</label>
                <input type="password" autoComplete="new-password" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone <span className="normal-case text-[10px] font-normal text-[#C4BAAF]">(required if no email — used for login)</span></label>
              <input autoComplete="off" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 9876543210" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Role</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.role_id || ""} onChange={e => setForm({ ...form, role_id: e.target.value })}>
                <option value="">Select Role</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchens</label>
              <div className="space-y-2 max-h-32 overflow-y-auto border border-[#E5E1DB] rounded-lg p-2">
                {kitchens.map(k => (
                  <label key={k.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={(form.kitchen_ids || []).includes(k.id)}
                      onChange={e => {
                        const ids = form.kitchen_ids || [];
                        setForm({ ...form, kitchen_ids: e.target.checked ? [...ids, k.id] : ids.filter(x => x !== k.id) });
                      }} />
                    {k.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_admin || false} onChange={e => setForm({ ...form, is_admin: e.target.checked })} />
                Admin User
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.can_issue_outside_menu || false} onChange={e => setForm({ ...form, can_issue_outside_menu: e.target.checked })} />
                Can issue outside menu
              </label>
              {modal.mode === "edit" && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                  Active
                </label>
              )}
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---- KITCHEN MODAL ---- */}
      {modal?.type === "kitchen" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Kitchen`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen Name *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">District *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.district || ""} onChange={e => setForm({ ...form, district: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">State *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.state || ""} onChange={e => setForm({ ...form, state: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Address</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---- RESET PASSWORD MODAL ---- */}
      {resetPwdModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C]">Reset Password</h2>
              <button onClick={() => setResetPwdModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">New Password *</label>
                <input type="password" autoFocus
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleResetPwd()} />
              </div>
              {pwdError && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{pwdError}</p>}
              <div className="flex gap-3">
                <button onClick={() => setResetPwdModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleResetPwd} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Reset</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {ConfirmModal}
    </div>
  );
}
